// Where the person's Me lives, for the web and the phone app: while it lives
// on their Hub (or moves), Me's pages say so instead and the workspaces go on.
// In the desktop app that shows this PC's screen too (the window bridge's
// `places`), the window goes there instead: personal data is on the Hub. An
// app that opens personal data on its own screen wherever it is kept
// (`places.personalAlwaysHere`) goes there from every page of Me, at the
// same page: this screen shows only the workspaces. The direct messages under
// /me are the cloud's, not personal data: they stay on this screen always.
//
// - ["me", "home"]: `GET /api/me/home`, read again on `me_home.changed`.
//   While Me moves it also says how far (the Hub tells the cloud every few
//   seconds and the cloud tells the screen at most every 5 s).
import { useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { m } from "@trameria/cloud-i18n";
import {
  type Api,
  buttonClass,
  defineScreenPart,
  type MeAway,
  MeAwayPanel,
  MeMoveProgress,
  PersonalDataKinds,
  useApi,
} from "@trameria/ui-core";
import { useEffect } from "react";
import { desktopFrame, HandToApp } from "../desktop-frame";

interface MeHome {
  home: "cloud" | "hub" | "switching";
  hub: { name: string } | null;
  /** While it moves: how far, as the Hub last told (null until it does). */
  progress?: { percent: number; etaSeconds: number | null } | null;
}

const meHomeKey = ["me", "home"] as const;
const meHomeQuery = (api: Api) => ({
  queryKey: meHomeKey,
  queryFn: () => api<MeHome>("/api/me/home"),
  staleTime: 60_000,
});

const useMeHome = () => useQuery(meHomeQuery(useApi())).data;

/** Whether the person's Me lives elsewhere than this cloud now (on a Hub, or moving). */
export function useMeAway(): boolean {
  const home = useMeHome();
  if (personalAlwaysHere()) return true;
  return home !== undefined && home.home !== "cloud";
}

/** The desktop app opens personal data on its own screen, wherever it is kept. */
const personalAlwaysHere = () =>
  desktopFrame !== null ||
  window.trameriaDesktop?.places?.personalAlwaysHere === true;

export const meHomePart = defineScreenPart({
  id: "me-home",
  realtime: {
    "me_home.changed": (_payload, { qc }) =>
      void qc.invalidateQueries({ queryKey: meHomeKey }),
  },
});

/** In place of Me's pages: the desktop app's window goes to this PC's screen. */
function OpenHere({ open }: { open: () => void }) {
  // frontend-development: moving the window is a side effect, not a read / none once the rail reaches this PC's screen by itself
  useEffect(() => open(), [open]);
  return (
    <MeAwayPanel
      title={m.web_me_on_hub_title()}
      body={m.web_me_opening_here()}
      detail={<PersonalDataKinds small align="center" />}
    />
  );
}

/**
 * In place of Me's pages in an app that opens personal data on its own
 * screen: the window goes to the same page there (nothing is drawn meanwhile).
 */
function OpenSamePageHere({ open }: { open: (path?: string) => void }) {
  // frontend-development: moving the window is a side effect, not a read / none: the cloud's screen has no personal data in this app
  useEffect(
    () => open(`${window.location.pathname}${window.location.search}`),
    [open],
  );
  return null;
}

function Cover() {
  const home = useMeHome();
  // Inside the app's screen: the app shows it (desktop-frame.tsx).
  if (desktopFrame) return <HandToApp />;
  const places = window.trameriaDesktop?.places;
  if (places && personalAlwaysHere())
    return <OpenSamePageHere open={places.openPersonal} />;
  if (home?.home === "hub" && places)
    return <OpenHere open={places.openPersonal} />;
  if (home?.home === "switching")
    return (
      <MeAwayPanel
        title={m.web_me_moving_title()}
        body={m.web_me_moving_body()}
        detail={
          <div className="flex flex-col items-center gap-4">
            <MeMoveProgress
              percent={home.progress?.percent ?? 0}
              etaSeconds={home.progress?.etaSeconds ?? null}
              align="center"
            />
            <PersonalDataKinds small align="center" />
            {places && (
              <button
                type="button"
                className={buttonClass()}
                onClick={() => places.openPersonal()}
              >
                {m.web_me_moving_open_here()}
              </button>
            )}
          </div>
        }
      />
    );
  return (
    <MeAwayPanel
      title={m.web_me_on_hub_title()}
      body={
        home?.hub
          ? m.web_me_on_hub_body({ hub: home.hub.name })
          : m.web_me_on_hub_body_unnamed()
      }
      detail={<PersonalDataKinds small align="center" />}
    />
  );
}

/**
 * The cloud's own pages under /me (conversations/part.tsx): the personal
 * direct messages and a topic by id are workspace things, not personal data,
 * so they stay here wherever Me lives.
 */
export const cloudsOwnMePath = (path: string) =>
  /^\/me\/(?:dms|topics)(?:\/|$)/.test(path);

/**
 * `useMeAway` for Me's frame: on the cloud's own pages under /me, Me's
 * sidebar is drawn whole (its favorites and labels) while Me's data is here,
 * as on the desktop app's own screen beside it.
 */
function useAwayHere(): boolean {
  const away = useMeAway();
  const home = useMeHome();
  const path = useRouterState({ select: (s) => s.location.pathname });
  return away && !(cloudsOwnMePath(path) && home?.home === "cloud");
}

export const webMeAway: MeAway = {
  useAway: useAwayHere,
  Cover,
  stays: cloudsOwnMePath,
};
